import { isMobile } from 'react-device-detect';
import { Language } from 'store/slices/language-slice';

interface RewardsMessages {
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  rewardsBalance: {
    heading: string;
    tooltip: string;
    receive: string;
    more: string;
    bitcoin: string;
    redeemButton: string;
    blockedNavigation: string;
  };
  btcRedemptionModal: {
    heading: string;
    headingLoading: string;
    loadingSubText: string;
    earned: string;
    worth: string;
    added: string;
    backButton: string;
    altTextSuccessImage: string;
    portfolioButton: string;
  };
  oopsModal: {
    heading: string;
    subheading: string;
    whitelist: (siteUrl: string | undefined) => string;
    altTextImage: string;
    backButton: string;
  };
  tiersModal: {
    points: string;
    currentTier: string;
    benefitsHeading: string;
  };
  reviewModal: {
    altTextImage: string;
    heading: string;
    content: Function;
    leaveReview: string;
  };
  surveyModal: {
    altTextImage: string;
    heading: string;
    content: Function;
  };
  shareModal: {
    heading: string;
    content: (
      companyName: string | undefined,
      siteUrl: string | undefined,
      currencySymbol: string,
      rewardPointRate: number,
      refereeRewardAmount: number,
      refereeMinBuyAmount: number,
      referrerRewardPoints: number,
    ) => string;
    altTextWhatsapp: string;
    altTextFacebook: string;
    altTextTwitter: string;
    altTextEmail: string;
    headerWhatsapp: string;
    headerFacebook: string;
    copied: string;
    headerTwitter: string;
    headerEmail: string;
    textCopiedAlert: string;
  };
  shareLinks: {
    whatsAppMessage: Function;
    twitterMessage: Function;
    facebookMessage: Function;
    emailMessage: Function;
  };
  rewardTypes: {
    signUp: string;
    completeAccountVerification: string;
    makeFirstInvestment: string;
    makeInvestment: string;
    referAFriend: string;
    leaveRating: string;
    completeASurvey: string;
    // TODO: add other rewards types here by key once returned on backend, to cater for translations in the future
    // Task: https://revix.atlassian.net/browse/RVXV2-386
  };
  rewardsProgress: {
    currentLevel: string;
    nextLevel: string;
    seeAllButton: string;
    benefitsHeading: string;
    noBenefits: string;
    comingSoon: string;
  };
  rewardsPoints: {
    heading: string;
    learnMoreButton: string;
    tooltip: string;
    completedActions: string;
    whatToDo: string;
    reward: string;
    rewards: string;
    pointsMobile: string;
    earned: string;
    pointsEarned: string;
  };
  rewardsHistory: {
    heading: string;
    action: string;
    points: string;
    pointsMobile: string;
    date: string;
    name: string;
    status: string;
    investedAlt: string;
    investedLessAlt: string;
    statusTooltip: (assetAmount: string) => string;
    noData: string;
    lessThanTooltip: (refereeFunctionalProductCode: string, minimumBuyAmount: number) => string;
    statusName: (status: string, currencySymbol: string, investmentAmount: number) => string;
  };
  promos: {
    heading: string;
    share: string;
    share1: string;
    share2: string;
    share3: string;
    share4: string;
    shareCode: string;
    promo: string;
    promoMobile: string;
    rewardsPoints: string;
    enterHere: string;
    placeholderNonMobile: string;
    submit: string;
    placeholder: string;
    bitcoinAlt: string;
  };
  explained: {
    heading: string;
    tcButton: string;
    faqButton: string;
    perform: string;
    convert: string;
    move: string;
    earn: string;
    earnMessage: string;
    levelUp: string;
    levelUpMessage: string;
    redeem: string;
    redeemMessage: (
      currencySymbol: string,
      currencyValue: number,
      points: number,
    ) => string;
  };
}

type Message = {
  [key in Language]: RewardsMessages;
};

const messages: Message = {
  en: {
    seo: {
      title: 'Altify - Rewards',
      description:
        'Effortlessly invest in crypto with one of our Bundles. Investing has never been this easy.',
      keywords:
        'investing, investment platform, cryptocrurency, top 10 cryptocurrency, top cryptocurrency, top performing crypto, diversification, diversified investing',
    },
    rewardsBalance: {
      heading: 'Rewards Points balance',
      more: 'more',
      receive: 'to convert to',
      bitcoin: 'Bitcoin',
      tooltip:
        'These points are the total amount of Altify Rewards Points that you have not yet converted to Bitcoin.',
      redeemButton: 'Redeem Bitcoin',
      blockedNavigation:
        'Your Altify Rewards Points are being redeemed, please wait for the transaction to complete.',
    },

    oopsModal: {
      heading: 'Oops!',
      subheading: "That's not ready yet",
      whitelist: (siteUrl: string | undefined) =>
        `Whitelist ${siteUrl} with your email provider to stay on top of upcoming promotions.`,
      altTextImage: 'Oops',
      backButton: 'Back to portfolio',
    },
    btcRedemptionModal: {
      heading: 'Congratulations!',
      headingLoading: 'Redeeming your points',
      loadingSubText: `You’ve worked hard, and now its time to reap the reward.<br/>Your points are being converted to Bitcoin.<br/><br/>Please wait while this transaction is processed`,
      earned: 'You have earned ',
      worth: 'BTC worth ',
      added: 'This has been added to your Bitcoin balance',
      backButton: 'Back to rewards',
      portfolioButton: 'Back to portfolio',
      altTextSuccessImage: 'Btc Added',
    },
    shareModal: {
      heading: 'Share your referral link',
      content: (
        companyName: string | undefined,
        siteUrl: string | undefined,
        currencySymbol: string,
        rewardPointRate: number,
        refereeRewardAmount: number,
        refereeMinBuyAmount: number,
        referrerRewardPoints: number,
      ) => `
                <span style="font-weight:600">Invite friends and you both get rewarded.</span>
                <br />
                <br />
                Earn <span style="font-weight:600">${referrerRewardPoints} ${companyName} Reward Points</span> worth ${currencySymbol}${rewardPointRate * referrerRewardPoints} when you introduce someone to Altify and they make their <span style="font-weight:600">first investment of ${currencySymbol}${refereeMinBuyAmount} or more</span> into <span style="font-weight:600">one</span> of our products. <br><br>The person you introduce will also receive ${currencySymbol}${refereeRewardAmount} straight into their Altify account. <br> <br> <a href="${siteUrl}/legal/terms-of-service" target="0" class="underline">Terms and conditions apply.</a>
                `,
      altTextWhatsapp: 'WhatsApp',
      altTextFacebook: 'Facebook',
      altTextTwitter: 'Twitter',
      altTextEmail: 'Email',
      headerWhatsapp: 'WhatsApp',
      headerFacebook: 'Facebook',
      headerTwitter: 'X',
      headerEmail: 'Email',
      copied: 'Copied To Clipboard',
      textCopiedAlert: 'Copied to clipboard',
    },
    shareLinks: {
      whatsAppMessage: (
        promoLink: string,
        reward: string,
        minimumInvestment: string,
      ) => {
        return `I wanted to introduce you to Altify. If you sign-up for a free account via ${promoLink} and make an investment, you’ll get ${reward} deposited straight into your Altify account when you make your first investment of ${minimumInvestment} or more. They make investing effortless and offer a family of investment products that include fractionalised gold bar tokens, 3 ready-made diversified crypto Bundles, Bitcoin, Ethereum and a USD stablecoin.%0D%0A%0D%0ASo, whether you’re new to crypto investing or looking to keep diversifying your portfolio Altify can help.`;
      },
      twitterMessage: (promoLink: string, reward: string) => {
        return `%40AltifyInvest+makes+crypto+investing+easy+and+effortless.+Sign-up+via+${promoLink}%2C+open+a+free+account%2C+make+an+investment%2C+and+you%27ll+get+${reward}+deposited+straight+into+your+Altify+account.%0D%0A`;
      },
      facebookMessage: (
        promoLink: string,
        reward: string,
        minimumInvestment: string,
      ) => {
        return `Our+world+is+getting+increasingly+digital%2C+and+with+Altify%2C+your+investments+will+be+too.+If+you+sign-up+via+${promoLink}%2C+open+a+free+account+and+make+a+first+investment+of+${minimumInvestment}+or+more+and+you%E2%80%99ll+get+${reward}+deposited+straight+into+your+Altify+account.+They+make+investing+effortless+and+offer+a+family+of+investment+products+that+include+fractionalised+gold+bar+tokens%2C+3+ready-made+diversified+crypto+Bundles%2C+Bitcoin+and+a+USD+stablecoin.`;
      },

      emailMessage: (
        promoLink: string,
        reward: string,
        minimumInvestment: string,
      ) => {
        return `I wanted to introduce you to Altify. If you sign-up via this link ${promoLink}, open a free account and make a first investment of ${minimumInvestment} or more with them and you’ll get ${reward} deposited straight into your Altify account. They make investing effortless and offer a family of investment products that include fractionalised gold bar tokens, 3 ready-made diversified crypto Bundles, Bitcoin, Ethereum and a USD stablecoin. \n\n\nSo, whether you’re new to crypto investing or looking to keep diversifying your portfolio Altify can help.`;
      },
    },
    tiersModal: {
      points: 'points',
      benefitsHeading: 'Benefits',
      currentTier: 'Current Tier',
    },
    reviewModal: {
      altTextImage: 'review-modal',
      heading: 'We would love to hear your experience',
      leaveReview: 'Leave a review',
      content: () => `
            Please note that your Altify Points will be allocated to your account between <span style="font-weight:600">2-5 working days</span> after completion.
            `,
    },
    surveyModal: {
      altTextImage: 'review-modal',
      heading: 'Help us make Altify even better',
      content: () => `
            We send out surveys via email, so keep an eye out for survey emails and don’t unsubscribe from our mailing list.
            <br />
            <br />
            Please note that your Altify Points will be allocated to your account between <span style="font-weight:600">2-5 working days</span> after completion.
            `,
    },
    rewardTypes: {
      signUp: 'Sign up',
      completeAccountVerification: 'Complete account verification',
      makeFirstInvestment: 'Make your first investment',
      makeInvestment: 'Make an investment',
      referAFriend: 'Refer a friend',
      leaveRating: 'Leave a rating and review about Altify',
      completeASurvey: 'Complete a survey',
    },
    rewardsProgress: {
      currentLevel: 'Your Level',
      nextLevel: 'Next Level',
      seeAllButton: 'See all levels',
      benefitsHeading: 'Benefits',
      noBenefits: 'No Benefits',
      comingSoon: 'Coming soon',
    },
    rewardsPoints: {
      heading: 'Earn Altify Points',
      learnMoreButton: 'Learn more',
      whatToDo: 'What to do',
      reward: 'Reward',
      rewards: 'Rewards history',
      tooltip: 'total tooltip',
      pointsMobile: 'Points',
      earned: 'Earned',
      completedActions: 'Completed actions',
      pointsEarned: 'Total lifetime Points',
    },
    rewardsHistory: {
      heading: 'Your referrals',
      action: 'Action',
      points: 'Rewards Points',
      pointsMobile: 'Points',
      date: 'Date',
      name: 'Name',
      status: 'Status',
      investedAlt: 'invested',
      investedLessAlt: 'invested-less',
      statusTooltip: (assetAmount: string) =>
        `You will be rewarded with Points once your friend has invested more than ${assetAmount}`,
      noData: 'No referrals',
      lessThanTooltip: (refereeFunctionalProductCode: string, minimumBuyAmount: number) =>
        `Your friend needs to make a single investment more than ${refereeFunctionalProductCode}${minimumBuyAmount} for you to get rewarded.`,
      statusName: (status: string, currencySymbol: string, investmentAmount: number) => {
        switch (status) {
          case 'SIGNEDUP':
            return 'Signed up';
          case 'INVESTEDLESS':
            return `Invested less than ${currencySymbol}${investmentAmount}`;
          case 'INVESTEDMORE':
            return `Invested`;
          default:
            return status;
        }
      },
    },
    promos: {
      heading: 'Referral and promo codes',
      share: 'Refer a friend',
      share1: 'You get ',
      share2: ' points worth ',
      share3: 'Your friend gets ',
      share4: ' cash.',
      shareCode: 'Share code',
      rewardsPoints: 'Rewards Points',
      promo: 'Enter a promo code below',
      promoMobile: 'Promo code',
      enterHere: 'Enter here',
      placeholder: 'Enter promo code',
      placeholderNonMobile: 'Enter here',
      submit: 'Submit',
      bitcoinAlt: 'bitcoin',
    },
    explained: {
      heading: 'How it works',
      tcButton: "View T&C's",
      faqButton: 'View FAQs',
      perform: 'Perform reward earning actions',
      convert: 'Convert reward points to Bitcoin',
      move: 'Move up rewards levels and collect rewards',
      earn: 'Earn',
      earnMessage: `<p class="pl-4">Points by completing specific actions.<p>`,
      levelUp: 'Level up',
      levelUpMessage: `<p class="pl-4">by accummulating Points and unlock lower fees and other great rewards.<p>`,
      redeem: 'Redeem',
      redeemMessage: (
        currencySymbol: string,
        currencyValue: number,
        points: number,
      ) => {
        return `<p class="pl-4 ${isMobile ? 'w-64' : ''
          }">each time you earn ${points} Points<br /> you can redeem them for ${currencySymbol}${currencyValue} worth of Bitcoin.<p>`;
      },
    },
  },
};

export default messages;
